import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Wifi, Loader2, LogOut } from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { parseUserAgent } from "@/utils/userAgentParser";
import DeviceIcon from "@/components/sessions/DeviceIcon";
import { useActiveSessions } from "@/hooks/admin/useActiveSessions";

const ActiveSessionsCard = () => {
  const { sessions, isLoading, terminateSession } = useActiveSessions();

  // Get current session token to prevent self-termination
  const currentToken = localStorage.getItem("session_token");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wifi className="h-5 w-5 text-primary" />
          Actieve Sessies
          <Badge variant="secondary" className="ml-1">{sessions.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-muted-foreground text-center py-6">Geen actieve sessies</p>
        ) : (
          <div className="border rounded-lg overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Gebruiker</TableHead>
                  <TableHead>Apparaat</TableHead>
                  <TableHead>Ingelogd sinds</TableHead>
                  <TableHead>Verloopt</TableHead>
                  <TableHead className="text-right">Actie</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map((session) => {
                  const device = parseUserAgent(session.user_agent);
                  const isCurrent = session.session_token === currentToken;

                  return (
                    <TableRow key={session.id}>
                      <TableCell className="font-medium">
                        <div>
                          {session.medewerker_naam}
                          {isCurrent && (
                            <Badge variant="outline" className="ml-2 text-xs">Jij</Badge>
                          )}
                        </div>
                        {session.medewerker_username && (
                          <span className="text-xs text-muted-foreground">{session.medewerker_username}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <DeviceIcon
                            browser={device.browser}
                            os={device.os}
                            deviceType={device.deviceType}
                            className="h-4 w-4 text-muted-foreground"
                          />
                          <span className="text-sm">
                            {device.browser} {device.browserVersion} / {device.os}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {session.created_at
                          ? format(new Date(session.created_at), "d MMM HH:mm", { locale: nl })
                          : "-"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(session.expires_at), "d MMM HH:mm", { locale: nl })}
                      </TableCell>
                      <TableCell className="text-right">
                        {!isCurrent && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => terminateSession.mutate(session.session_token)}
                            disabled={terminateSession.isPending}
                            title="Sessie beëindigen"
                          >
                            <LogOut className="h-4 w-4 mr-1" />
                            Beëindigen
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActiveSessionsCard;
